import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { formatTime } from '@/lib/i18n/format'
import { currentLocale } from '@/lib/i18n'

import { sameLocalDay } from '../format'
import { ChatAvatar } from './chat-avatar'
import { ChatDateChip, ChatSystemChip } from './chat-chips'
import { ChatMenu } from './chat-menu'

export interface ChatTimelineItem {
  id: string
  createdAt: string
  kind: 'user' | 'system'
  body: string
  authorName: string
  authorAvatarUrl: string | null
  senderId: string | null
  mine: boolean
  canDelete: boolean
}

interface ChatTimelineProps {
  items: ChatTimelineItem[]
  deletingId: string | null
  onDelete: (item: ChatTimelineItem) => void
}

/**
 * Shared message timeline: date dividers, system join chips and mine/other
 * bubbles. Deletion is offered through the bubble menu, not only long press.
 */
export function ChatTimeline({
  items,
  deletingId,
  onDelete,
}: ChatTimelineProps) {
  const { t } = useTranslation()

  return (
    <ol className="chat-timeline" aria-label={t('chat.timeline.label')}>
      {items.map((item, index) => {
        const previous = index > 0 ? items[index - 1] : null
        const showDivider =
          Boolean(item.createdAt) &&
          (!previous ||
            !sameLocalDay(
              new Date(previous.createdAt),
              new Date(item.createdAt),
            ))

        return (
          <Fragment key={item.id}>
            {showDivider ? <ChatDateChip value={item.createdAt} /> : null}
            <li className="chat-timeline__item">
              {item.kind === 'system' ? (
                <ChatSystemChip text={item.body} />
              ) : (
                <ChatMessageBubble
                  item={item}
                  deleting={deletingId === item.id}
                  onDelete={() => onDelete(item)}
                />
              )}
            </li>
          </Fragment>
        )
      })}
    </ol>
  )
}

interface ChatMessageBubbleProps {
  item: ChatTimelineItem
  deleting: boolean
  onDelete: () => void
}

function ChatMessageBubble({
  item,
  deleting,
  onDelete,
}: ChatMessageBubbleProps) {
  const { t } = useTranslation()
  const time = formatTime(item.createdAt, currentLocale())
  const author =
    item.senderId !== null ? (
      <Link
        to={`/users/${item.senderId}`}
        className="chat-message__author"
        aria-label={t('chat.message.author', { name: item.authorName })}
      >
        {item.authorName}
      </Link>
    ) : (
      <span className="chat-message__author">{item.authorName}</span>
    )

  return (
    <div className={`chat-message ${item.mine ? 'chat-message--mine' : ''}`}>
      {!item.mine ? (
        <ChatAvatar
          name={item.authorName}
          src={item.authorAvatarUrl}
          size="sm"
        />
      ) : null}
      <div className="chat-message__content">
        <div className="chat-message__bubble">
          <div className="chat-message__head">{author}</div>
          <p className="chat-message__body">{item.body}</p>
          <div className="chat-message__foot">
            <time
              className="chat-message__time"
              dateTime={item.createdAt || undefined}
            >
              {time}
            </time>
          </div>
          {item.canDelete ? (
            <div className="chat-message__actions">
              <ChatMenu
                items={[
                  {
                    key: 'delete',
                    labelKey: 'chat.message.delete',
                    onSelect: onDelete,
                    danger: true,
                    disabled: deleting,
                  },
                ]}
                label={t('chat.message.options')}
              />
            </div>
          ) : null}
        </div>
      </div>
      {item.mine ? (
        <ChatAvatar
          name={item.authorName}
          src={item.authorAvatarUrl}
          size="sm"
        />
      ) : null}
    </div>
  )
}
